/**
 * Rundowns and playlists as resources (PLAN §6.5, §10.1): create, list, get,
 * soft-delete. Each is the head row of its own partition (`@norna/domain/row.js`
 * explains why), so what these routes return are rows — the same shape the
 * row API and the WS echo carry.
 *
 * A create is a convenience over a creation patch: the server mints the HLC
 * and writes the head row. The client MAY supply the id (a ULID it generated),
 * which makes a retried create idempotent — a create whose id already exists
 * with the same kind returns the existing row.
 */
import { playlistTimingWireSchema } from './rowFields.js';
import { ordSchema, ulidSchema } from '@norna/domain/primitives.js';
import { rowWireSchema } from './rowDocument.js';
import { z } from 'zod';
import { cursorWireSchema, PAGE_LIMIT_DESCRIPTION, PAGE_TOKEN_DESCRIPTION, pageLimitWireSchema, pageTokenWireSchema } from './rows.js';

const nameSchema = z.string().min(1).max(500);

export const createRundownRequestWireSchema = z
  .object({
    /** Client-generated id for an idempotent retry; the server mints one when absent. */
    rundownId: ulidSchema.optional(),
    name: nameSchema,
    /** The playlist to place it in, or null for a stand-alone rundown. */
    playlistId: ulidSchema.nullable(),
    /** Position among the playlist's rundowns; null = after the last. */
    ord: ordSchema.nullable(),
  })
  .strict();
export type CreateRundownRequestWire = z.infer<typeof createRundownRequestWireSchema>;

export const rundownResponseWireSchema = z.object({ rundown: rowWireSchema }).strict();

/** `GET /rundowns/{rundownId}`: the head row, and the cursor to start a `changedSince` catch-up from. */
export const getRundownResponseWireSchema = z.object({ rundown: rowWireSchema, cursor: cursorWireSchema }).strict();

export const listRundownsQueryWireSchema = z
  .object({
    playlistId: ulidSchema.optional().describe('Only the rundowns of this playlist.'),
    pageToken: pageTokenWireSchema.optional().describe(PAGE_TOKEN_DESCRIPTION),
    limit: pageLimitWireSchema.optional().describe(PAGE_LIMIT_DESCRIPTION),
    includeDeleted: z.enum(['true', 'false']).optional().describe('Include soft-deleted rundowns (to offer an undelete). Default false.'),
  })
  .strict();

export const listRundownsResponseWireSchema = z
  .object({ rundowns: z.array(rowWireSchema), nextPageToken: pageTokenWireSchema.nullable() })
  .strict();

export const createPlaylistRequestWireSchema = z
  .object({
    playlistId: ulidSchema.optional(),
    name: nameSchema,
    timing: playlistTimingWireSchema,
  })
  .strict();
export type CreatePlaylistRequestWire = z.infer<typeof createPlaylistRequestWireSchema>;

export const playlistResponseWireSchema = z.object({ playlist: rowWireSchema }).strict();

export const getPlaylistResponseWireSchema = z.object({ playlist: rowWireSchema, cursor: cursorWireSchema }).strict();

export const listPlaylistsQueryWireSchema = z
  .object({
    pageToken: pageTokenWireSchema.optional().describe(PAGE_TOKEN_DESCRIPTION),
    limit: pageLimitWireSchema.optional().describe(PAGE_LIMIT_DESCRIPTION),
    includeDeleted: z.enum(['true', 'false']).optional().describe('Include soft-deleted playlists. Default false.'),
  })
  .strict();

export const listPlaylistsResponseWireSchema = z
  .object({ playlists: z.array(rowWireSchema), nextPageToken: pageTokenWireSchema.nullable() })
  .strict();

export const playlistPathWireSchema = z.object({ playlistId: ulidSchema.describe('The playlist’s id (a ULID).') }).strict();

